import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, Card, EmptyState } from "@/components/Shell";
import { MapPinned, ChevronLeft } from "lucide-react";

export function ProjectsList() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [err, setErr] = useState("");
  useEffect(() => { api.get("/projects").then(r => setItems(r.data)).catch(e=>setErr(formatApiError(e))); }, []);
  const filtered = q ? items.filter(i => (i.project_id||"").toLowerCase().includes(q.toLowerCase())) : items;

  return (
    <div className="p-4 md:p-8" data-testid="projects-page">
      <PageHeader title="Projects / Circuit IDs" subtitle="Material currently mapped to each customer circuit." />
      <div className="mb-3"><input data-testid="proj-search" placeholder="Search project/circuit ID..." value={q} onChange={e=>setQ(e.target.value)} className="inp max-w-sm" /></div>
      {err && <div className="text-rose-600 text-sm mb-3">{err}</div>}
      {filtered.length === 0 ? <EmptyState title="No projects yet" icon={MapPinned} desc="Projects are auto-created when an engineer submits a usage update." /> :
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm data-table">
              <thead className="bg-zinc-50"><tr>
                <th className="px-4 py-3 text-left">Project/Circuit</th>
                <th className="px-4 py-3 text-left">Customer</th>
                <th className="px-4 py-3 text-left">Address</th>
                <th className="px-4 py-3 text-left">Created</th>
              </tr></thead>
              <tbody>
                {filtered.map(p => (
                  <tr key={p.id} className="border-t border-zinc-100 hover:bg-zinc-50">
                    <td className="px-4 py-3"><Link to={`/projects/${p.id}`} className="num font-semibold text-[#F97316] hover:underline">{p.project_id}</Link></td>
                    <td className="px-4 py-3">{p.customer_name || "—"}</td>
                    <td className="px-4 py-3 text-zinc-500">{p.site_address || "—"}</td>
                    <td className="px-4 py-3 text-zinc-500 num text-xs">{(p.created_at||"").slice(0,10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      }
      <style>{`.inp { width:100%; border:1px solid #E4E4E7; background:#fff; border-radius:.375rem; padding:.5rem .75rem; font-size:.85rem; outline:none } .inp:focus { box-shadow: 0 0 0 2px #F97316; border-color: transparent }`}</style>
    </div>
  );
}

export function ProjectDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get(`/projects/${id}`).then(r=>setP(r.data)).catch(e=>setErr(formatApiError(e))); }, [id]);

  if (err) return <div className="p-6 text-rose-600 text-sm">{err}</div>;
  if (!p) return <div className="p-6 text-sm text-zinc-500">Loading…</div>;

  const bts = (p.installed_serials || []).filter(s => s.usage_location === "BTS Side");
  const ce = (p.installed_serials || []).filter(s => s.usage_location === "CE Side");
  const popSerials = (p.installed_serials || []).filter(s => s.usage_location === "POP");
  const others = (p.installed_serials || []).filter(s => !["BTS Side","CE Side","POP"].includes(s.usage_location));

  return (
    <div className="p-4 md:p-8" data-testid="project-detail">
      <button onClick={()=>nav(-1)} className="text-xs text-zinc-500 hover:text-[#F97316] mb-3 inline-flex items-center gap-1"><ChevronLeft size={14}/> Back</button>
      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-widest font-bold text-[#F97316]">Circuit / Project</div>
        <h1 className="font-display text-3xl font-bold mt-1 num">{p.project_id}</h1>
        <div className="text-sm text-zinc-500 mt-1">{p.customer_name || "—"} · {p.site_address || "—"}</div>
        {p.total_cost !== undefined && (
          <div className="mt-3 inline-flex items-center gap-2 bg-[#F97316]/10 border border-[#F97316]/20 px-3 py-1.5 rounded-md">
            <span className="text-[10px] uppercase tracking-widest font-bold text-[#F97316]">Total Material Cost</span>
            <span className="font-display font-bold num text-[#F97316]">₹{p.total_cost.toLocaleString("en-IN")}</span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SerialTable title="BTS Side" rows={bts} />
        <SerialTable title="CE Side" rows={ce} />
        {popSerials.length > 0 && <SerialTable title="POP" rows={popSerials} />}
        <SerialTable title="Other Locations" rows={others} />
        <Card className="p-5">
          <div className="font-display font-semibold mb-3">Non-Serialized Material</div>
          {(p.non_serialized_usages||[]).length === 0 ?
            <div className="text-xs text-zinc-500">None</div> :
            <table className="w-full text-sm data-table">
              <thead><tr><th className="text-left">SKU</th><th className="text-left">Product</th><th className="text-right">Qty</th><th className="text-left">Location</th></tr></thead>
              <tbody>
                {p.non_serialized_usages.map((u, i) => (
                  <tr key={i} className="border-t border-zinc-100">
                    <td className="num">{u.sku}</td>
                    <td>{u.product_name}</td>
                    <td className="num text-right font-bold">{u.quantity}</td>
                    <td className="text-zinc-500">{u.usage_location}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          }
        </Card>
        <Card className="p-5 lg:col-span-2">
          <div className="font-display font-semibold mb-3">Recovery History</div>
          {(p.recoveries||[]).length === 0 ?
            <div className="text-xs text-zinc-500">No recoveries from this project</div> :
            <div className="space-y-2">
              {p.recoveries.map(r => (
                <div key={r.id} className="flex items-center justify-between text-sm border-b border-zinc-100 pb-2">
                  <div>
                    <div className="font-semibold">{r.engineer_name}</div>
                    <div className="text-xs text-zinc-500">{(r.recovery_date||"").slice(0,10)} · {r.recovered_from} · {r.items?.length || 0} items</div>
                  </div>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-zinc-100 text-zinc-700">{r.status}</span>
                </div>
              ))}
            </div>
          }
        </Card>
      </div>
    </div>
  );
}

function SerialTable({ title, rows }) {
  return (
    <Card className="p-5">
      <div className="font-display font-semibold mb-3">{title} <span className="text-zinc-400 text-sm font-normal">({rows.length})</span></div>
      {rows.length === 0 ? <div className="text-xs text-zinc-500">No items</div> :
        <table className="w-full text-sm data-table">
          <thead><tr><th className="text-left">SKU</th><th className="text-left">Product</th><th className="text-left">Serial</th><th className="text-left">Status</th></tr></thead>
          <tbody>
            {rows.map((s, i) => (
              <tr key={i} className="border-t border-zinc-100">
                <td className="num">{s.sku}</td>
                <td>{s.product_name}</td>
                <td className="num text-xs">{s.serial_no}</td>
                <td className="text-zinc-500">{s.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      }
    </Card>
  );
}
